/**
 * DELIVERY mechanics for a chat turn: how the client decides a POST actually
 * became a run, and how the offline outbox is replayed once the connection is
 * back. Split out of `chat.ts` (500-LOC ceiling) and deliberately free of Solid
 * signals — these are the rules that decide whether a message the user typed is
 * kept, retried or dropped, so they are unit-tested on their own.
 */
import { type ApiError, isErr, type Result } from '@4f/shared';
import { z } from 'zod';
import { readEnvelope, toApiError } from '../api/envelope.ts';
import { isTransportFailure, MalformedResponseError } from '../api/errors.ts';
import type { Outbox, OutboxEntry } from './outbox.ts';

/** Schedule `fn` after `ms`; returns a canceller (injected so tests own the clock). */
export type ChatScheduler = (fn: () => void, ms: number) => () => void;

/**
 * How long an outbox replay waits for one run's SSE view to close before giving up on
 * it and moving to the next queued turn. Sized as a ceiling on a chat turn, matching
 * the edge's own per-conversation lock TTL — the flush cannot usefully outlast the
 * lock it is waiting on.
 */
export const OUTBOX_DRAIN_TIMEOUT_MS = 300_000;

/**
 * What a refusal means for the QUEUE. Dropping a turn is irreversible — the text
 * only exists on this device — so the verdict is deliberately conservative:
 * `rejected` is reserved for a server that declined this turn ON ITS MERITS and
 * will decline it again forever.
 *
 *  - `blocked` — this CONVERSATION is busy with the previous reply. Its turns keep
 *    their place and their order; every other conversation carries on.
 *  - `retry-later` — nothing about this turn: the edge is unreachable, the session
 *    needs renewing, we are being rate-limited, the deploy is rolling. The whole
 *    replay stops and the queue keeps its order.
 *  - `rejected` — over quota, moderated, malformed, or a conversation that no
 *    longer exists. Replaying it forever helps nobody, so it is dropped and the
 *    user is told.
 */
export type RefusalVerdict = 'blocked' | 'retry-later' | 'rejected';

/** Statuses that are about the CALLER's session or the edge's health, not the turn. */
const RETRYABLE_STATUS = new Set([401, 403, 408, 425, 429]);

/**
 * Classify a refusal. The HTTP status leads, because it is the one field every
 * layer between here and the edge agrees on; the code covers what never reached a
 * status at all (a thrown fetch → `network_error`).
 *
 * Getting this wrong in the lenient direction DELETES the user's message: before,
 * anything outside a three-code allow-list counted as a real "no", so a `401` on a
 * session that expired while the device was offline — the outbox's own scenario —
 * silently wiped every queued turn on the account.
 */
export function verdictFor(status: number, error: ApiError): RefusalVerdict {
  if (error.code === 'conversation_busy' || status === 409) {
    return 'blocked';
  }
  // The two failures that never carry a status: nothing reached the edge, or
  // something that is not the edge answered.
  if (isTransportFailure(error)) {
    return 'retry-later';
  }
  if (status >= 500 || RETRYABLE_STATUS.has(status)) {
    return 'retry-later';
  }
  return 'rejected';
}

/** A turn the edge did not turn into a run, with the status that says why. */
export interface Refusal {
  readonly error: ApiError;
  /** The HTTP status, or `0` when the response never arrived. */
  readonly status: number;
}

/**
 * What this response refused with, or `null` when it really is the run's SSE view.
 *
 * `safeFetch` resolves `ok` for every HTTP status — only a thrown fetch is `err` — so
 * "did this turn actually leave?" cannot be read off `isErr`, and it cannot be read
 * off the status either (the edge's outcomes are envelope-encoded). It is read off
 * the content type, and the test is DELIVERY, not refusal: exactly one shape means
 * the run started, and everything else is a turn that did not happen.
 *
 * That direction matters. Sniffing for `application/json` instead treats a `502`
 * error page from the ingress — the single likeliest reason a turn needs replaying at
 * all — as a delivery: the send goes silent and the outbox drops the turn unsent.
 */
export async function refusalOf(response: Response): Promise<Refusal | null> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/event-stream')) {
    return null;
  }
  if (contentType.includes('application/json')) {
    const envelope = await readEnvelope(response, z.unknown());
    // An `ok: true` body here is contract drift, not a run — and the body is now
    // consumed either way, so it must never fall through to the stream reader.
    const error = isErr(envelope)
      ? envelope.error
      : toApiError(new MalformedResponseError('expected the run stream'));
    return { error, status: response.status };
  }
  // An ingress error page: release it rather than leave a body open on every
  // refused turn (this path is the `502`/`504` one, i.e. the common one).
  void response.body?.cancel().catch(() => undefined);
  return {
    error: toApiError(
      new MalformedResponseError(`HTTP ${response.status}, ${contentType || 'no content-type'}`),
    ),
    status: response.status,
  };
}

/**
 * Read a detached run's SSE view to its terminal event and discard it — which is
 * also how the flush waits for the conversation to be free again.
 *
 * BOUNDED: these replayed sends carry no abort signal (the turns outlive the view
 * that typed them), so without a ceiling one stalled run holds the rest of the queue
 * for as long as it hangs — and a hang is exactly the case the backend lock's own
 * TTL exists for. On expiry the reader is cancelled and the flush moves on; the run
 * itself is server-side and unaffected.
 */
export async function drain(response: Response, schedule: ChatScheduler): Promise<void> {
  const body = response.body;
  if (!body) {
    return;
  }
  const reader = body.getReader();
  const giveUp = schedule(() => {
    void reader.cancel().catch(() => undefined);
  }, OUTBOX_DRAIN_TIMEOUT_MS);
  try {
    for (;;) {
      const { done } = await reader.read();
      if (done) {
        break;
      }
    }
  } catch {
    // A dropped connection mid-replay is not worth failing the flush over: the
    // run is server-side, and the history reload is the authority.
  } finally {
    giveUp();
  }
}

/** What {@link replayOutbox} needs — all injected, so the loop is driven with no store. */
export interface ReplayDeps {
  /** POST one queued turn. No abort signal: a replayed turn outlives the view. */
  readonly send: (conversationId: string, text: string) => Promise<Result<Response, ApiError>>;
  readonly outbox: Outbox;
  readonly schedule: ChatScheduler;
  /**
   * The conversation the user is LOOKING at, or `null`. A rejection is only ever
   * acted on for this one: an error raised for a thread that is not on screen has
   * nowhere honest to render (the store has a single `error` signal, and the view
   * would attach it — paywall included — to whatever companion happens to be
   * open). Another conversation's rejected turn is left QUEUED instead, so it is
   * refused visibly, under her own thread, the next time it is replayed.
   */
  readonly openConversationId: () => string | null;
  /** Report a turn the server actually REJECTED (it has just been dropped). */
  readonly onRefused: (error: ApiError) => void;
}

/**
 * Replay the WHOLE outbox, oldest first — the reconnect path. Each replayed run is
 * DETACHED server-side, so its SSE view is not rendered (interleaving several runs
 * into one live stream would be incoherent); the caller's history reload is what
 * surfaces them.
 *
 * Every conversation is replayed, not just the one on screen. Scoping this to the
 * open thread is a silent hole: turns typed to another companion (or to any
 * companion, when the tab is reopened before one is picked) sit in durable storage
 * forever, shown as "sending" in a view the user has to guess they must visit.
 *
 * Three rules keep the queue honest, and getting any of them wrong destroys the
 * data this module exists to protect:
 *
 *  - **A refusal is not an `err`.** `safeFetch` resolves `ok` for every HTTP status
 *    — only a thrown fetch is `err` — so branching on `isErr` alone counts a `409`
 *    as a delivery and drops the turn unsent, silently. {@link refusalOf} reads the
 *    envelope instead.
 *  - **The edge serializes runs per conversation.** Firing one conversation's queue
 *    back to back means turn 2 lands while turn 1's run still holds it and is
 *    refused `409`. So each replay is DRAINED (the SSE view ends on the run's
 *    terminal event, which is exactly "the thread is free again") before the next is
 *    sent, and a `409` that slips through anyway BLOCKS that conversation — its
 *    remaining turns stay queued, in order, for the next flush — without holding up
 *    any other conversation.
 *  - **Only a turn the server refused ON ITS MERITS is dropped** ({@link verdictFor}).
 *    Anything else — the session needs renewing, the limiter said slow down, the
 *    deploy is rolling, nothing reached the edge at all — stops the replay with the
 *    queue intact.
 *
 * @returns the conversations that took at least one replayed turn (their history is
 * now stale).
 */
export async function replayOutbox(deps: ReplayDeps): Promise<ReadonlySet<string>> {
  // Snapshot: `remove` below replaces the queue, and a `send` on another companion
  // can enqueue mid-replay — neither may reorder the loop that is running.
  const pending = [...deps.outbox.list()];
  const delivered = new Set<string>();
  const blocked = new Set<string>();

  /** Apply one refusal to the queue; `stop` ends the whole replay. */
  const settle = (entry: OutboxEntry, refusal: Refusal): 'stop' | 'next' => {
    const verdict = verdictFor(refusal.status, refusal.error);
    if (verdict === 'retry-later') {
      return 'stop';
    }
    if (verdict === 'blocked' || deps.openConversationId() !== entry.conversationId) {
      // Either the thread is busy, or the rejection belongs to a companion the
      // user is not looking at — keep it queued rather than delete a message whose
      // refusal nobody would ever see.
      blocked.add(entry.conversationId);
      return 'next';
    }
    // A real "no" on the open thread (over quota, red line): dropping it is what
    // stops an endless replay of a turn the server will never accept, and the user
    // is told — an error they can act on beats a silent disappearance.
    deps.outbox.remove(entry.id);
    deps.onRefused(refusal.error);
    return 'next';
  };

  for (const entry of pending) {
    // Her previous reply is still running on THIS thread — everything queued behind
    // it must keep its place, while other conversations carry on.
    if (blocked.has(entry.conversationId)) {
      continue;
    }
    const opened = await deps.send(entry.conversationId, entry.text);
    if (isErr(opened)) {
      // `safeFetch` only errs on a THROWN fetch (`network_error`), but classify it
      // rather than assume: a client that ever surfaced a domain error this way
      // must not be replayed against forever.
      if (settle(entry, { error: opened.error, status: 0 }) === 'stop') {
        return delivered;
      }
      continue;
    }
    const refusal = await refusalOf(opened.value);
    if (refusal) {
      if (settle(entry, refusal) === 'stop') {
        return delivered;
      }
      continue;
    }
    deps.outbox.remove(entry.id);
    delivered.add(entry.conversationId);
    // Drain, do not cancel: the run is detached, so cancelling only blinds US to
    // it — it keeps the conversation busy either way, and the next queued turn
    // would be refused.
    await drain(opened.value, deps.schedule);
  }
  return delivered;
}
